<template>
  <div :id="id" ref="dropzoneEl" class="vue-dropzone dropzone">
    <slot v-if="useCustomSlot"></slot>
  </div>
</template>

<script>
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'

Dropzone.autoDiscover = false

export default {
  name: 'vue-dropzone',
  props: {
    id: { type: String, required: true },
    options: { type: Object, required: true },
    useCustomSlot: { type: Boolean, default: false },
  },
  emits: [
    'vdropzone-file-added',
    'vdropzone-success',
    'vdropzone-error',
    'vdropzone-max-files-exceeded',
    'vdropzone-sending',
    'vdropzone-removed-file',
    'vdropzone-processing',
    'vdropzone-upload-progress',
    'vdropzone-complete',
    'vdropzone-queue-complete',
  ],
  data() {
    return {
      dropzone: null,
    }
  },
  mounted() {
    const opts = {
      ...this.options,
      // If using custom slot, suppress the default message
      ...(this.useCustomSlot ? { dictDefaultMessage: '' } : {}),
    }

    this.dropzone = new Dropzone(this.$refs.dropzoneEl, opts)

    // Wire dropzone events to Vue emits (matching vue2-dropzone API)
    this.dropzone.on('addedfile', (file) => {
      this.$emit('vdropzone-file-added', file)
    })
    this.dropzone.on('success', (file, response) => {
      this.$emit('vdropzone-success', file, response)
    })
    this.dropzone.on('error', (file, message, xhr) => {
      this.$emit('vdropzone-error', file, message, xhr)
    })
    this.dropzone.on('maxfilesexceeded', (file) => {
      this.$emit('vdropzone-max-files-exceeded', file)
    })
    this.dropzone.on('sending', (file, xhr, formData) => {
      this.$emit('vdropzone-sending', file, xhr, formData)
    })
    this.dropzone.on('removedfile', (file) => {
      this.$emit('vdropzone-removed-file', file)
    })
    this.dropzone.on('processing', (file) => {
      this.$emit('vdropzone-processing', file)
    })
    this.dropzone.on('uploadprogress', (file, progress, bytesSent) => {
      this.$emit('vdropzone-upload-progress', file, progress, bytesSent)
    })
    this.dropzone.on('complete', (file) => {
      this.$emit('vdropzone-complete', file)
    })
    this.dropzone.on('queuecomplete', () => {
      this.$emit('vdropzone-queue-complete')
    })
  },
  beforeUnmount() {
    if (this.dropzone) {
      this.dropzone.destroy()
      this.dropzone = null
    }
  },
  methods: {
    // --- vue2-dropzone compatible API ---
    removeFile(file) {
      this.dropzone.removeFile(file)
    },
    // Queue a File built in code (a converted upload) as if it were
    // dropped: runs `accept`, emits file-added, lands in getQueuedFiles().
    addFile(file) {
      this.dropzone.addFile(file)
    },
    removeAllFiles(cancelUploads) {
      this.dropzone.removeAllFiles(cancelUploads)
    },
    processQueue() {
      this.dropzone.processQueue()
    },
    getQueuedFiles() {
      return this.dropzone.getQueuedFiles()
    },
    getAcceptedFiles() {
      return this.dropzone.getAcceptedFiles()
    },
    getRejectedFiles() {
      return this.dropzone.getRejectedFiles()
    },
    setOption(key, value) {
      this.dropzone.options[key] = value
    },
    disable() {
      this.dropzone.disable()
    },
    enable() {
      this.dropzone.enable()
    },
  },
}
</script>
